import React, { useState } from "react";
import { Box, Text, useBreakpointValue } from "@chakra-ui/react";
import ContactTableRow from "./ContactTableRow";
import { EmptyState, ContactTableHeader, ContactDrawer, ContactTableFooter } from "./ContactTableParts";
import useCurrencySymbols from "./useCurrencySymbols";

/** Column header labels for the contact table grid. */
const COLUMN_HEADERS = ["Name", "Date", "Amount", "Reference"];

/**
 * Builds responsive cell padding styles for header columns.
 * @param {boolean} isMobile - Whether the viewport is mobile-sized
 * @returns {Object} Chakra style props for cell padding
 */
function headerCellSx(isMobile) {
    return { padding: isMobile ? "12px 14px" : "14px 18px" };
}

/**
 * Builds responsive typography styles for header labels.
 * @param {boolean} isMobile - Whether the viewport is mobile-sized
 * @returns {Object} Chakra style props for header text
 */
function headerTextSx(isMobile) {
    return {
        color: "rgba(255, 255, 255, 0.8)",
        fontWeight: 600,
        fontSize: isMobile ? "0.7rem" : "0.75rem",
        textTransform: "uppercase",
        letterSpacing: "0.5px"
    };
}

/**
 * Main contact table component with header, body rows, footer, and a detail drawer.
 * @param {Object} props
 * @param {Array} props.contactCards - List of contact card objects
 * @param {string} props.title - Header title text
 * @param {string} props.nameField - Reservation field name for the first column (e.g. "artist_name")
 * @param {string} props.headerLabel - Label for the first table column header
 * @param {string} props.emptyText - Text displayed when no contacts exist
 * @param {string} props.footerLabel - Label for the footer counter
 * @param {React.ElementType} props.icon - react-icons component for header and empty state
 * @returns {React.ReactElement}
 */
function ContactTable({ contactCards, title, nameField, headerLabel, emptyText, footerLabel, icon: Icon }) {
    const isMobile = useBreakpointValue({ base: true, sm: false });
    const [refIndexCopied, setRefIndexCopied] = useState(null);
    const [selectedContact, setSelectedContact] = useState(null);
    const { getCurrencySymbol } = useCurrencySymbols(contactCards);

    if (!contactCards || contactCards.length === 0) {
        return <EmptyState Icon={Icon} emptyText={emptyText} isMobile={isMobile} />;
    }

    return (
        <Box>
            <ContactTableHeader Icon={Icon} title={title} isMobile={isMobile} />
            <ContactTableBody
                contactCards={contactCards}
                headerLabel={headerLabel}
                nameField={nameField}
                isMobile={isMobile}
                refIndexCopied={refIndexCopied}
                setRefIndexCopied={setRefIndexCopied}
                getCurrencySymbol={getCurrencySymbol}
                onRowClick={setSelectedContact}
            />
            <ContactDrawer selectedContact={selectedContact} onClose={() => setSelectedContact(null)} isMobile={isMobile} getCurrencySymbol={getCurrencySymbol} />
            <ContactTableFooter label={footerLabel} count={contactCards.length} isMobile={isMobile} />
        </Box>
    );
}

/**
 * Table body with column headers and contact rows.
 * @param {Object} props
 * @param {Array} props.contactCards - Contact cards data
 * @param {string} props.headerLabel - First column header label
 * @param {string} props.nameField - Reservation field for the name column
 * @param {boolean} props.isMobile - Mobile viewport flag
 * @param {number|null} props.refIndexCopied - ID of last copied reference
 * @param {Function} props.setRefIndexCopied - Setter for copied ref state
 * @param {Function} props.getCurrencySymbol - Currency symbol resolver
 * @param {Function} props.onRowClick - Row click handler
 * @returns {React.ReactElement}
 */
function ContactTableBody({ contactCards, headerLabel, nameField, isMobile, refIndexCopied, setRefIndexCopied, getCurrencySymbol, onRowClick }) {
    return (
        <Box
            background="rgba(255, 255, 255, 0.04)"
            backdropFilter="blur(15px)"
            borderRadius="12px"
            border="1px solid rgba(255, 255, 255, 0.12)"
            overflow="hidden"
        >
            <ContactTableColumnHeaders headerLabel={headerLabel} isMobile={isMobile} />
            <ContactTableRows
                contactCards={contactCards}
                nameField={nameField}
                isMobile={isMobile}
                refIndexCopied={refIndexCopied}
                setRefIndexCopied={setRefIndexCopied}
                getCurrencySymbol={getCurrencySymbol}
                onRowClick={onRowClick}
            />
        </Box>
    );
}

/**
 * Renders the column headers row for the contact table grid.
 * @param {Object} props
 * @param {string} props.headerLabel - Label for the first column
 * @param {boolean} props.isMobile - Mobile viewport flag
 * @returns {React.ReactElement}
 */
function ContactTableColumnHeaders({ headerLabel, isMobile }) {
    return (
        <Box
            display="grid"
            gridTemplateColumns="2fr 1.5fr 1.5fr 2fr"
            background="rgba(255, 255, 255, 0.08)"
            backdropFilter="blur(10px)"
            borderBottom="1px solid rgba(255, 255, 255, 0.15)"
        >
            {[headerLabel, ...COLUMN_HEADERS.slice(1)].map((text) => (
                <Box key={text} {...headerCellSx(isMobile)}>
                    <Text {...headerTextSx(isMobile)}>{text}</Text>
                </Box>
            ))}
        </Box>
    );
}

/**
 * Renders all contact table data rows.
 * @param {Object} props
 * @param {Array} props.contactCards - Contact cards data
 * @param {string} props.nameField - Reservation field for name
 * @param {boolean} props.isMobile - Mobile viewport flag
 * @param {number|null} props.refIndexCopied - ID of last copied reference
 * @param {Function} props.setRefIndexCopied - Setter for copied ref state
 * @param {Function} props.getCurrencySymbol - Currency symbol resolver
 * @param {Function} props.onRowClick - Row click handler
 * @returns {React.ReactElement}
 */
function ContactTableRows({ contactCards, nameField, isMobile, refIndexCopied, setRefIndexCopied, getCurrencySymbol, onRowClick }) {
    return (
        <Box>
            {contactCards.map((contact, index) => (
                <ContactTableRow
                    key={index}
                    contact={contact}
                    index={index}
                    total={contactCards.length}
                    nameField={nameField}
                    isMobile={isMobile}
                    refIndexCopied={refIndexCopied}
                    setRefIndexCopied={setRefIndexCopied}
                    currencySymbol={getCurrencySymbol(contact)}
                    onRowClick={onRowClick}
                />
            ))}
        </Box>
    );
}

export default ContactTable;
